import React, { Suspense, lazy, useCallback, useMemo } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import PremiumLock from '../../components/Common/PremiumLock';
import { useAuth } from '../../context/AuthContext';
import { useScreenType } from '../../hooks/useScreenType';
import { useCompanyResults } from './useCompanyResults';
import { ResultsPageProps } from './resultsModel';
import DesktopResults from './DesktopResults';

// Le desktop ne télécharge jamais la vue mobile.
const MobileResults = lazy(() => import('./MobileResults'));

/**
 * Résultats des sociétés cotées : une période (T1 / S1 / 9M / Annuel) comparée à la même
 * période de l'exercice précédent, regroupée par secteur. Ex-« Tableau Comparatif des
 * Fondamentaux » de l'Analyse Globale, sorti de cette page au rythme quotidien.
 *
 * Conteneur : droits, données et choix de période. Le rendu est délégué à <DesktopResults />
 * (table comparative) ou <MobileResults /> (cartes, mobile et tablette).
 */
export default function CompanyResultsScreen(): React.JSX.Element {
  const { hasPermission } = useAuth();
  const allowed = hasPermission('view_advanced_data');
  const { isDesktop } = useScreenType();
  const { periods, selection, setSelection, results, loading, error } = useCompanyResults(allowed);

  const periodOptions = useMemo(
    () => [...new Set((periods?.data ?? []).map(o => o.period_type))],
    [periods]
  );

  const yearOptions = useMemo(
    () => (periods?.data ?? [])
      .filter(o => o.period_type === selection?.period_type)
      .sort((a, b) => b.exercice - a.exercice),
    [periods, selection?.period_type]
  );

  const selectedOption = yearOptions.find(o => o.exercice === selection?.exercice) ?? null;

  const handlePeriodChange = useCallback((periodType: string) => {
    const years = (periods?.data ?? []).filter(o => o.period_type === periodType).map(o => o.exercice);
    if (years.length === 0) return;
    // Conserver l'exercice s'il existe pour la nouvelle période, sinon le plus récent.
    const exercice = selection && years.includes(selection.exercice) ? selection.exercice : Math.max(...years);
    setSelection({ period_type: periodType, exercice });
  }, [periods, selection, setSelection]);

  const handleExerciceChange = useCallback((exercice: number) => {
    if (selection) setSelection({ ...selection, exercice });
  }, [selection, setSelection]);

  if (!allowed) {
    return (
      <SiteLayout>
        <Header icon='assessment' title='Résultats des sociétés' />
        <div style={{ padding: isDesktop ? 40 : 16, maxWidth: 900, margin: '0 auto' }}>
          <PremiumLock
            title='Résultats des sociétés cotées'
            description="Comparez les résultats trimestriels, semestriels et annuels des sociétés de la BRVM avec l'exercice précédent, secteur par secteur. Disponible avec le plan Kanea Starter ou supérieur."
          />
        </div>
      </SiteLayout>
    );
  }

  const pageProps: ResultsPageProps = {
    periodOptions,
    yearOptions,
    selection,
    onPeriodChange: handlePeriodChange,
    onExerciceChange: handleExerciceChange,
    selectedOption,
    partialCoverage: !loading && !!selectedOption && !!periods && selectedOption.coverage < periods.meta.min_coverage,
    results,
    loading,
    error,
  };

  return (
    <SiteLayout>
      <Header icon='assessment' title='Résultats des sociétés' />
      {isDesktop ? (
        <DesktopResults {...pageProps} />
      ) : (
        <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Chargement...</div>}>
          <MobileResults {...pageProps} />
        </Suspense>
      )}
    </SiteLayout>
  );
}
